// Maximus Bot (iGotSolutions): remembered-panel titles and which rows belong to a bot.

export type MemoryScopeRow = {
  userId: string;
  botId: string | null;
  kind: string;
  subject?: string | null;
};

/**
 * A profile key the model stored as an id (gB-uAU) is not a title.
 * Words and phrases are.
 */
export function isOpaqueMemoryKey(key: string): boolean {
  const trimmed = key.trim().replace(/[:.]+$/u, "");
  if (!trimmed || trimmed.startsWith("legacy-document:")) return true;
  // A phrase is a title. Tokens such as gB-uAU or W1haVy are ids.
  if (/\s/u.test(trimmed)) return false;
  if (!/^[A-Za-z0-9_-]{2,64}$/u.test(trimmed)) return false;
  return /[-_\d]/u.test(trimmed) || /[a-z][A-Z]|[A-Z][a-z]/u.test(trimmed);
}

/** Drop a stored id from the front of a fact so the panel does not lead with it. */
export function memoryDisplayFact(fact: string, key?: string | null): string {
  let text = fact.replace(/\r\n/gu, "\n").trim();
  if (key && isOpaqueMemoryKey(key)) {
    const escaped = key.trim().replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
    text = text.replace(new RegExp(`^${escaped}\\s*[:\\-–—]\\s*`, "u"), "").trim();
  }
  const lines = text.split("\n");
  const first = lines[0]?.trim() ?? "";
  if (first && isOpaqueMemoryKey(first)) text = lines.slice(1).join("\n").trim();
  return text || fact.trim();
}

export function memoryDisplayTitle(fact: string, key?: string | null): string {
  if (key && !isOpaqueMemoryKey(key)) return key.trim();
  const line = memoryDisplayFact(fact, key)
    .split("\n")
    .map((part) => part.trim())
    .find((part) => part && !isOpaqueMemoryKey(part));
  if (!line) return "Memory";
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
}

/**
 * Chloe's panel shows Chloe's rows and this user's shared profile or journal.
 * Another bot's rows, and a team bucket stored as kind "shared" with no user
 * subject, stay out. Rows are never deleted; they are only omitted here.
 */
export function memoryRowVisibleToBot(
  row: MemoryScopeRow,
  scope: { userId: string; botId: string },
): boolean {
  if (row.userId !== scope.userId || row.kind === "history") return false;
  if (row.botId === scope.botId) return true;
  if (row.botId) return false;
  if (row.kind === "shared" && row.subject !== "user") return false;
  return (
    row.kind === "fact" || row.kind === "journal" || row.kind === "profile" || row.kind === "shared"
  );
}
